import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Glasses, Plus, Ruler } from 'lucide-react';
import { PERMISSIONS } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Card, CardHeader } from '@/components/ui/Card';
import { LoadingState } from '@/components/ui/LoadingState';
import { Pagination } from '@/components/ui/Pagination';
import { useBranch } from '@/hooks/useBranch';
import { usePermissions } from '@/hooks/usePermissions';
import { FuturePlaceholderCard } from '@/modules/customers/components/FuturePlaceholderCard';
import { useCustomerOpticalJobsQuery } from '../api/opticalJobs.api';
import { OPTICAL_BUILDER_PERMISSIONS } from '../permissions';
import { trackingErrorMessage } from '../tracking/errors';
import { JobList } from './JobList';

const PAGE_SIZE = 5;

/**
 * The customer's optical orders, job by job, and the way into a new one.
 *
 * Every job the user can reach, in any of their branches, newest first; each opens its tracking screen. A new order is offered only to someone holding every grant the builder needs, and stays offered while the history loads or if it cannot be read. Someone who may not read optical jobs keeps the reserved panel the profile had before.
 */
export function OpticalOrdersCard({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const { can, canAll } = usePermissions();
  const { branches } = useBranch();
  const [page, setPage] = useState(1);
  const canRead = can(PERMISSIONS.OPTICAL_VIEW);
  const canCreate = canAll(OPTICAL_BUILDER_PERMISSIONS);
  const history = useCustomerOpticalJobsQuery(canRead ? customerId : '', {
    page,
    pageSize: PAGE_SIZE,
  });

  if (!canRead) {
    return (
      <FuturePlaceholderCard
        icon={Ruler}
        title={t('customers.sections.optical')}
        emptyTitle={t('customers.placeholders.noMeasurements')}
        description={t('customers.placeholders.futureModule')}
      />
    );
  }

  const create = canCreate ? (
    <Link
      to={`/customers/${customerId}/optical-orders/new`}
      className="bg-brand-600 hover:bg-brand-700 inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium text-white transition-colors"
    >
      <Plus className="size-4" aria-hidden />
      {t('opticalOrders.entry.create')}
    </Link>
  ) : null;

  const items = history.data?.items ?? [];

  return (
    <Card>
      <CardHeader
        title={t('opticalOrders.entry.title')}
        subtitle={t('opticalOrders.entry.subtitle')}
        action={create ?? undefined}
        wrapAction
      />
      {history.isError ? (
        <div className="px-5 py-4">
          <Alert tone="error">{trackingErrorMessage(t, history.error)}</Alert>
        </div>
      ) : history.isPending ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <p className="text-muted flex items-start gap-3 px-5 py-5 text-sm">
          <Glasses className="text-brand-600 mt-0.5 size-5 shrink-0" aria-hidden />
          {canCreate ? t('opticalOrders.entry.body') : t('opticalOrders.history.none')}
        </p>
      ) : (
        <>
          <JobList jobs={items} showCustomer={false} showBranch={branches.length > 1} />
          {history.data ? (
            <Pagination
              pagination={history.data.pagination}
              onPageChange={setPage}
              isBusy={history.isFetching}
            />
          ) : null}
        </>
      )}
    </Card>
  );
}
